import {
    TextField as _TextField,
    InputProps,
    Autocomplete,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {FC} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {getValueFromNestedObject} from "../../../../utils/langUtils.ts";

const TextField = styled(_TextField)`
    padding: 0.4rem 0.7rem;
`;


interface TransformOptionType<T> {
    inputFromKey(value: number | string | undefined): T;
    outputToKey(value: T | undefined): number | string | undefined;
    optionEqualToValue(option: T, value: T): boolean;
    multipleOptionEqualToValue(option: T, value: string): boolean;
}

type IdOptionType = { id: number | string }

export const transformIdOptionType: TransformOptionType<IdOptionType> = {
    inputFromKey: (value: string | number | undefined) => value ? {id: value} : {id: ''},
    outputToKey: (value: IdOptionType | undefined) => value?.id ? value.id : undefined,
    optionEqualToValue: (option: IdOptionType, value: IdOptionType) => {
        return option?.id == value?.id
    },
    multipleOptionEqualToValue: (option: IdOptionType, value: string) => {
        return option.id == value
    },
}

type SimpleOptionType = number | string

export const transformSimpleType: TransformOptionType<SimpleOptionType> = {
    inputFromKey: (value: string | number | undefined) => value || '',
    outputToKey: (value: SimpleOptionType | undefined) => value,
    optionEqualToValue: (option: SimpleOptionType, value: SimpleOptionType) => option == value,
    multipleOptionEqualToValue: (option: SimpleOptionType, value: SimpleOptionType) => option == value,
}

export type FormSelectProps = {
    name: string;
    label: string;
    options: Array<IdOptionType> | undefined;
    optionsLoading: boolean;
    multiple?: boolean;
    transform: TransformOptionType<IdOptionType>;
    optionLabelMap?: (option: IdOptionType | undefined) => string;
    onValueChange?: (option: IdOptionType | IdOptionType[] | undefined) => void;
} & InputProps;

const FormSelect: FC<FormSelectProps> = ({
                                             name,
                                             label,
                                             options = [],
                                             optionsLoading,
                                             multiple,
                                             required,
                                             transform = transformIdOptionType,
                                             optionLabelMap,
                                             onValueChange,
                                             ...otherProps
                                         }) => {
    const {
        control,
        formState: {errors},
    } = useFormContext();

    const error = getValueFromNestedObject(errors, name)

    return (
        <Controller
            control={control}
            name={name}
            render={({field: props}) => (
                <Autocomplete
                    fullWidth
                    disablePortal
                    options={options}
                    loading={optionsLoading}
                    multiple={multiple}
                    getOptionKey={transform.outputToKey}
                    getOptionLabel={(optionValue) => {
                        const foundOption = options.find((o) => transform.optionEqualToValue(o, optionValue));
                        return optionLabelMap ? optionLabelMap(foundOption) || '' : '';
                    }}
                    isOptionEqualToValue={transform.optionEqualToValue}
                    onChange={(_, newValue) => {
                        props.onChange(multiple ? newValue?.map(transform.outputToKey) : transform.outputToKey(newValue));
                        onValueChange && onValueChange(newValue ?? undefined);
                    }}
                    value={multiple ? props.value.map(transform.inputFromKey) : transform.inputFromKey(props.value)}
                    {...otherProps}
                    renderInput={(params) => (
                            <TextField
                                {...params}
                                margin="normal"
                                error={!!error}
                                variant="standard"
                                label={label}
                                required={required}
                                helperText={
                                    error ? (error?.message as unknown as string) : ''
                                }
                            />
                        )
                    }
                />
            )}
        />
    );
};

export default FormSelect;
